import { IconCheckRound } from '@shared/ui/icons'
import { useAlarmList, useReadAllAlarms, useReadOneAlarm } from '@features/notification'

// alarmType 라벨 매핑 (서버: bot / auth / system)
const ALARM_TYPE_LABEL: Record<string, string> = {
  bot: '[봇]',
  auth: '[인증]',
  system: '[시스템]',
}

const LEVEL_TITLE_COLOR: Record<string, string> = {
  '0': 'text-[#efe9e9]',
  '1': 'text-[#faea4d]',
  '3': 'text-[#f5a524]',
  '4': 'text-[#e71b0c]',
  '5': 'text-[#e71b0c]',
}

const getAlarmTypeLabel = (alarmType: string) => ALARM_TYPE_LABEL[alarmType] ?? `[${alarmType}]`
const getLevelColor = (level: string) => LEVEL_TITLE_COLOR[level] ?? 'text-[#efe9e9]'

// 헤더 벨 아래 뜨는 컴팩트 알림 팝업 — 최근 알림 미리보기 + 전체보기 진입
export const NotificationPopup = ({ onViewAll }: { onViewAll: () => void }) => {
  const { alarms } = useAlarmList()
  const { readAllAlarms } = useReadAllAlarms()
  const { readOneAlarm } = useReadOneAlarm()

  const isEmpty = alarms.length === 0
  const hasUnread = alarms.some(a => a.readStatus === 0)
  const preview = alarms.slice(0, 5)

  return (
    <div className="absolute right-0 top-full z-50 pt-2">
      <div className="flex w-[300px] flex-col rounded-[8px] bg-[#262d36] shadow-[0px_4px_8px_rgba(0,0,0,0.18),0px_8px_18px_rgba(0,0,0,0.28)]">
      {/* 상단: 모두 읽음 처리 */}
      <div className="flex items-center justify-end border-b border-white/[0.18] px-4 py-3">
        <button
          type="button"
          onClick={() => readAllAlarms()}
          disabled={isEmpty || !hasUnread}
          className="flex items-center gap-1.5 disabled:cursor-not-allowed disabled:opacity-30"
        >
          <span className="font-pretendard text-xs font-medium tracking-[-0.3px] text-font-basic">
            모두 읽음 처리
          </span>
          <span className="flex size-3.5 items-center justify-center">
            <IconCheckRound color="#efe9e9" />
          </span>
        </button>
      </div>

      {/* 리스트 */}
      {isEmpty ? (
        <div className="flex h-[180px] flex-col items-center justify-center gap-3">
          <div className="size-12 shrink-0">
            <img src="/images/icon-notepad.svg" alt="" className="size-full object-contain" />
          </div>
          <span className="font-pretendard text-sm tracking-[-0.35px] text-font-gray">
            알림 내역이 없습니다.
          </span>
        </div>
      ) : (
        <div className="flex max-h-[300px] flex-col overflow-y-auto px-4">
          {preview.map(alarm => {
            const isRead = alarm.readStatus === 1
            return (
              <div
                key={alarm.alarmId}
                role="button"
                tabIndex={0}
                onClick={!isRead ? () => readOneAlarm(alarm.alarmId) : undefined}
                onKeyDown={
                  !isRead
                    ? e => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault()
                          readOneAlarm(alarm.alarmId)
                        }
                      }
                    : undefined
                }
                className={`flex items-start border-b border-white/[0.18] py-3 last:border-b-0 ${
                  isRead ? 'opacity-[0.38]' : 'cursor-pointer'
                }`}
              >
                <div className="flex min-w-0 flex-1 flex-col">
                  <span
                    className={`font-pretendard text-sm font-semibold tracking-[-0.35px] ${getLevelColor(
                      alarm.level
                    )}`}
                  >
                    {getAlarmTypeLabel(alarm.alarmType)}
                  </span>
                  <p className="pt-1 font-pretendard text-xs tracking-[-0.3px] text-font-gray">
                    {alarm.message}
                  </p>
                  <span className="pt-1 font-pretendard text-xs tracking-[-0.3px] text-font-gray">
                    {alarm.createdAt}
                  </span>
                </div>
                <span className="flex size-[18px] shrink-0 items-center justify-center pl-2">
                  {!isRead && <IconCheckRound color="#efe9e9" />}
                </span>
              </div>
            )
          })}
        </div>
      )}

      {/* 하단: 전체 보기 */}
      <button
        type="button"
        onClick={onViewAll}
        className="flex items-center justify-end gap-1 border-t border-white/[0.18] px-4 py-3"
      >
        <span className="font-pretendard text-xs font-medium tracking-[-0.3px] text-font-gray">
          전체 보기 »
        </span>
      </button>
      </div>
    </div>
  )
}
